<template>
    <Teleport to="body">
        <div class="zs-help" :class="{ 'zs-help--open': helpOpen }">
            <div class="zs-help__backdrop" @click="closeHelp"></div>
            <aside class="zs-help__drawer" role="dialog" aria-label="Help and support">
                <header class="zs-help__head">
                    <span class="zs-help__title">
                        <i class="fa-regular fa-circle-question" />
                        Help &amp; Support
                    </span>
                    <button class="zs-help__close" type="button" aria-label="Close" @click="closeHelp">
                        <i class="fa-solid fa-xmark" />
                    </button>
                </header>

                <!-- Kept alive: once opened the iframe is never torn down (v-if stays true);
                     the drawer just hides via the parent's open class. So closing/reopening —
                     or navigating elsewhere in the app — never reloads or loses the form. -->
                <iframe v-if="helpEverOpened" :src="helpSrc" class="zs-help__frame" title="ZynoSuite Support" allow="clipboard-write" />
            </aside>
        </div>
    </Teleport>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted } from 'vue';

import { closeHelp, helpEverOpened, helpOpen, helpSrc } from '@/shared/helpers/help.helpers';

function onKeydown(e: KeyboardEvent) {
    // ESC only reaches us when focus is in the app shell (not inside the cross-origin
    // iframe), which is the case when dismissing via the backdrop area.
    if (e.key === 'Escape' && helpOpen.value) closeHelp();
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<style scoped>
@reference '#tw.css';

.zs-help {
    @apply fixed inset-0 z-[1000] pointer-events-none;
}

.zs-help--open {
    @apply pointer-events-auto;
}

.zs-help__backdrop {
    @apply absolute inset-0 bg-scrim opacity-0 transition-opacity duration-200;
}

.zs-help--open .zs-help__backdrop {
    @apply opacity-100;
}

.zs-help__drawer {
    @apply absolute top-0 right-0 h-full w-[480px] max-w-full bg-surface flex flex-col shadow-2xl;
    transform: translateX(100%);
    transition: transform 0.3s ease;
}

.zs-help--open .zs-help__drawer {
    transform: translateX(0);
}

.zs-help__head {
    @apply flex items-center justify-between px-4 h-14 shrink-0 border-b border-line;
}

.zs-help__title {
    @apply flex items-center gap-2 font-medium text-ink-2;
}

.zs-help__close {
    @apply w-8 h-8 flex items-center justify-center rounded-full text-ink-3 hover:bg-surface-3;
}

.zs-help__frame {
    @apply flex-1 w-full border-0;
}
</style>
